import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ChangeEvent, KeyboardEvent, Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";
import { getDurationMask, getLicensePlateMask } from "@frontend/utils/masks";

import { Input } from "./Input";
import { type InputDataProps, inputDataSchema } from "./Input.schema";
import { INPUT_TAG_NAME } from "./constants";

const INPUT_MASK_FACTORIES = {
  duration: getDurationMask,
  "license-plate": getLicensePlateMask,
} as const;

export interface InputHandle {
  disabled: (value?: boolean) => void;
  setError: (message?: string | null) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [INPUT_TAG_NAME]: { props: InputDataProps; handle: InputHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-input": HTMLElement & InputHandle;
  }
}

type InputSlots = {
  hasLeftSection: boolean;
  hasRightSection: boolean;
};

function readInputSlotted(el: HTMLElement): InputSlots {
  return {
    hasLeftSection: Boolean(el.querySelector(':scope > [slot="left"]')),
    hasRightSection: Boolean(el.querySelector(':scope > [slot="right"]')),
  };
}

export function RWCInput({
  ref,
  disabled: initialDisabled,
  value: initialValue,
  mask,
  hasLeftSection,
  hasRightSection,
  ...props
}: InputDataProps & Partial<InputSlots> & { ref?: Ref<InputHandle> }) {
  const host = useIslandHost();

  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const currentValue = useRef(initialValue ?? "");
  const committedValue = useRef(initialValue ?? "");

  const handle: InputHandle = {
    disabled: (value = true) => setDisabled(value),
    setError: (message = null) => setErrorMessage(message),
  };

  useImperativeHandle(ref, () => handle, []);

  const syncFormValue = useCallback(() => {
    getElementInternals(host)?.setFormValue(
      disabled ? null : currentValue.current,
    );
  }, [host, disabled]);

  useEffect(syncFormValue, [syncFormValue]);

  // Reinitializes the mask instance
  const resolvedMask = useMemo(
    () => (mask ? INPUT_MASK_FACTORIES[mask]() : undefined),
    [mask],
  );

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    currentValue.current = event.target.value;
    syncFormValue();
  }

  function handleValueChange(value: string) {
    currentValue.current = value;
    syncFormValue();
  }

  // On blur or Enter, once per edit: the native change never leaves the shadow
  function commit() {
    if (currentValue.current === committedValue.current) {
      return;
    }

    committedValue.current = currentValue.current;
    host?.setAttribute("value", currentValue.current);
    host?.dispatchEvent(
      new CustomEvent("input:change", {
        detail: { value: currentValue.current },
        bubbles: true,
      }),
    );
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      commit();
    }
  }

  return (
    <Input
      {...props}
      leftSection={hasLeftSection ? <slot name="left" /> : undefined}
      rightSection={hasRightSection ? <slot name="right" /> : undefined}
      mask={resolvedMask}
      defaultValue={initialValue}
      disabled={disabled}
      invalid={errorMessage != null}
      title={errorMessage ?? undefined}
      onChange={mask ? undefined : handleChange}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      onValueChange={mask ? handleValueChange : undefined}
    />
  );
}

componentManager.register(
  rwc(INPUT_TAG_NAME, {
    component: RWCInput,
    dataSchema: inputDataSchema,
    usesSlots: true,
    readSlotted: readInputSlotted,
    formAssociated: true,
  }),
);
